import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import Sidebar from "components/Sidebar";
import DefaultLayout from "layout/DefaultLayout";
import Page404 from "pages/page404/Page404";
import SigninLoader from "pages/signin/loader";
import SignupPassLoader from "pages/signup/pass/complete/loader";
import React, { Suspense } from "react";
import { Outlet, createBrowserRouter, redirect } from "react-router-dom";
import PrivateRoute from "./routes/PrivateRoute";
import ScrollToTop from "./routes/ScrollToTop";

const Signup = React.lazy(() => import("./pages/signup"));
const SignupForm = React.lazy(() => import("./pages/signup/form"));
const SignupComplete = React.lazy(() => import("./pages/signup/complete"));
const SignupPassComplete = React.lazy(() => import("./pages/signup/pass"));

const Matching = React.lazy(() => import("./pages/matching"));
const MatchingDetail = React.lazy(() => import("./pages/matching/detail"));
const News = React.lazy(() => import("./pages/news"));

const Community = React.lazy(() => import("./pages/community"));
const CommunityList = React.lazy(() => import("./pages/community/list"));
const CommunityDetail = React.lazy(() => import("./pages/community/detail"));
const CommunityWrite = React.lazy(() => import("./pages/community/write"));

const Mypage = React.lazy(() => import("./pages/mypage"));
const MypageEdit = React.lazy(() => import("./pages/mypage/edit"));
const MypageExpertEdit = React.lazy(() => import("./pages/mypage/expert"));
const MypageCommunity = React.lazy(() => import("./pages/mypage/community"));
const MypageMatching = React.lazy(() => import("./pages/mypage/matching"));
const MypageIBS = React.lazy(() => import("./pages/mypage/ibs"));
const Terms = React.lazy(() => import("./pages/mypage/term"));
const ServiceCenter = React.lazy(() => import("./pages/mypage/service-center"));
const MypageMembership = React.lazy(() => import("./pages/mypage/membership"));

const SelfIBS = React.lazy(() => import("./pages/selfIbs"));
const IBSResult = React.lazy(() => import("./pages/ibsResult"));

const AuthCallback = React.lazy(() => import("./pages/signin"));

const DataRoom = React.lazy(() => import("./pages/dataRoom"));
const DataRoomDetail = React.lazy(() => import("./pages/dataRoom/detail"));

const Membership = React.lazy(() => import("./pages/membership"));
const MembershipOrderSuccess = React.lazy(
  () => import("./pages/membership/order/success")
);
const MembershipOrderFail = React.lazy(
  () => import("./pages/membership/order/fail")
);
const MembershipBillingSuccess = React.lazy(
  () => import("./pages/membership/billing/success")
);

const loading = <div>화면을 불러오는 중 입니다.</div>;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

export const routes = createBrowserRouter([
  {
    path: "/",
    element: (
      <Suspense fallback={loading}>
        <QueryClientProvider client={queryClient}>
          <DefaultLayout>
            <ScrollToTop />
            <Outlet />
          </DefaultLayout>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </Suspense>
    ),
    errorElement: <Page404 />,
    children: [
      {
        index: true,
        loader: async () => redirect("/data-room"),
      },
      {
        path: "signup",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Signup />,
          },
          {
            element: <SignupForm />,
            path: "/signup/form",
          },
          {
            element: <SignupComplete />,
            path: "/signup/complete",
          },
          {
            element: <SignupPassComplete />,
            path: "/signup/pass/complete",
            loader: SignupPassLoader,
          },
        ],
      },
      {
        path: "matching",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Matching />,
          },
          {
            path: "detail/:id",
            element: (
              <PrivateRoute>
                <MatchingDetail />,
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: "News",
        element: <Outlet />,

        children: [
          {
            index: true,
            element: <News />,
          },
        ],
      },
      {
        path: "community",
        element: (
          <PrivateRoute>
            <Outlet />
          </PrivateRoute>
        ),
        children: [
          {
            index: true,
            element: <Community />,
          },
          {
            path: "list",
            element: <CommunityList />,
          },
          {
            path: "detail/:id",
            element: <CommunityDetail />,
          },
          {
            path: "write",
            element: <CommunityWrite />,
          },
          {
            path: "write/:id",
            element: <CommunityWrite />,
          },
        ],
      },
      {
        path: "mypage",
        element: (
          <div style={{ display: "flex" }}>
            <Sidebar />
            <Outlet />
          </div>
        ),
        children: [
          {
            index: true,
            element: <Mypage />,
          },
          {
            path: "/mypage/edit",
            element: <MypageEdit />,
          },
          {
            path: "/mypage/expert",
            element: <MypageExpertEdit />,
          },
          {
            path: "/mypage/matching",
            element: <MypageMatching />,
          },
          {
            path: "/mypage/activity",
            element: <MypageCommunity />,
          },
          {
            path: "/mypage/ibs",
            element: <MypageIBS />,
          },
          {
            path: "/mypage/membership",
            element: <MypageMembership />,
          },
        ],
      },
      {
        path: "/diagnosis",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <SelfIBS />,
          },
          {
            path: "/diagnosis/result/:ibsId",
            element: <IBSResult />,
          },
        ],
      },
      {
        path: "/membership",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Membership />,
          },
          {
            path: "/membership/order/success",
            element: <MembershipOrderSuccess />,
          },
          {
            path: "/membership/order/fail",
            element: <MembershipOrderFail />,
          },
          {
            path: "/membership/billing/success",
            element: <MembershipBillingSuccess />,
          },
        ],
      },
      {
        path: "/data-room",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <DataRoom />,
          },
          {
            path: "/data-room/detail/:id",
            element: <DataRoomDetail />,
          },
        ],
      },
      {
        path: "/signin/callback/:provider",
        loader: SigninLoader,
        element: <AuthCallback />,
      },
      {
        path: "/mypage/service-center",
        element: <ServiceCenter />,
      },
      {
        path: "/mypage/term",
        element: <Terms />,
      },
    ],
  },
]);
